<template>
  <div class="marketing-page">
    <!-- 顶部：行业选择 + 大师思想库入口 -->
    <el-card shadow="never" style="margin-bottom: 16px">
      <template #header>
        <div class="header-bar">
          <span>🎯 营销军师 —— 大师思维库驱动的获客话术引擎</span>
          <div class="hb-sel-wrap">
            <el-select v-model="industry" class="industry-sel" placeholder="选择行业" filterable clearable>
              <el-option v-for="n in industryNames" :key="n" :label="n" :value="n" />
            </el-select>
          </div>
        </div>
      </template>
      <el-alert type="success" :closable="false" show-icon
        title="孙宇晨流量操盘术 × 特劳特定位 × 华与华超级符号 × 营销心理学"
        description="话术由大师方法论组合生成：数字钩子（具体才可信）、定位差异化（一句话讲清你是谁）、心理学驱动（锚定/社会认同/互惠）。生成后可直接复制到「话术管理」或手动发送。" />
    </el-card>

    <el-row :gutter="16">
      <!-- 左列：话术工坊 + 角度推荐（手机整宽堆叠，不再两列挤压） -->
      <el-col :xs="24" :md="12">
        <el-card shadow="never" style="margin-bottom: 16px">
          <template #header>
            <div class="header-bar">
              <span>💬 话术工坊</span>
              <div>
                <el-radio-group v-model="kind" size="small" @change="onKindChange">
                  <el-radio-button label="dm">私信</el-radio-button>
                  <el-radio-button label="comment">评论</el-radio-button>
                  <el-radio-button label="followup">跟进</el-radio-button>
                </el-radio-group>
                <el-button size="small" type="primary" style="margin-left: 8px" :loading="copyLoading" @click="genCopy">⚡ 生成</el-button>
              </div>
            </div>
          </template>
          <div v-if="kind === 'dm'" class="opt-row">
            <el-select v-model="style" size="small" style="width: 140px">
              <el-option label="标准" value="standard" />
              <el-option label="钩子优先" value="hook" />
              <el-option label="温和" value="soft" />
            </el-select>
            <span class="opt-hint">数字钩子（真实数据，可选）：</span>
            <el-input v-model="statsText" size="small" style="width: 200px" placeholder="如 服务600+客户、10年老店回头客过半" />
          </div>
          <div v-if="kind === 'followup'" class="opt-row">
            <el-select v-model="day" size="small" style="width: 140px">
              <el-option label="第1天·破冰" :value="1" />
              <el-option label="第3天·痛点" :value="3" />
              <el-option label="第7天·方案" :value="7" />
              <el-option label="第14天·促单" :value="14" />
            </el-select>
            <span class="opt-hint">多幕剧节奏：每次只推一个信息点</span>
          </div>
          <div class="opt-row">
            <el-input-number v-model="count" size="small" :min="1" :max="20" /> <span class="opt-hint">条</span>
          </div>
          <div v-loading="copyLoading">
            <div v-for="(item, i) in copyItems" :key="i" class="copy-item">
              <div class="copy-text">{{ item }}</div>
              <el-button size="mini" type="primary" plain @click="copyText(item)">复制</el-button>
            </div>
            <el-empty v-if="!copyLoading && copyItems.length === 0" description="点击「生成」出话术" :image-size="60" />
          </div>
        </el-card>

        <el-card shadow="never">
          <template #header>
            <div class="header-bar">
              <span>🧭 线索角度推荐</span>
              <el-button size="small" type="primary" :loading="angleLoading" @click="genAngle">🔍 推荐</el-button>
            </div>
          </template>
          <el-input v-model="leadNote" type="textarea" :rows="3" placeholder="粘贴客户留言/评论内容，如：你们这个怎么收费？能先看看效果吗？" />
          <div v-if="angleResult" class="angle-result">
            <div class="angle-line"><b>痛点：</b>{{ angleResult.pain }}</div>
            <div class="angle-line"><b>推荐角度：</b>{{ angleResult.recommended }}</div>
            <div class="angle-line"><b>依据框架：</b>{{ angleResult.framework }}</div>
          </div>
        </el-card>

        <el-card shadow="never">
          <template #header>
            <div class="header-bar">
              <span>🎬 内容钩子（短视频开头）</span>
              <el-button size="small" type="primary" :loading="hookLoading" @click="genHook">⚡ 生成</el-button>
            </div>
          </template>
          <el-input v-model="hookTopic" size="small" placeholder="主题：拍什么（如：新店开业怎么让同城人知道）" style="margin-bottom: 8px" />
          <el-input v-model="hookMaterial" type="textarea" :rows="2" placeholder="素材：真实数字/案例/身份（如：开业3年、老客占六成、上过本地美食榜）" />
          <div v-if="hookResult" class="hook-result">
            <div class="angle-line"><b>公式：</b>{{ hookResult.formula }}</div>
            <div class="angle-line"><b>素材命中：</b>{{ hookResult.material_dims.join('、') }}</div>
            <div v-for="(v, i) in hookResult.variants" :key="i" class="hook-variant">
              <b>[{{ v.style }}]</b> {{ v.hook }}
              <el-button size="mini" type="primary" plain @click="copyText(v.hook)">复制</el-button>
            </div>
            <div class="master-warn">💡 {{ hookResult.tip }}</div>
          </div>
        </el-card>
      </el-col>

      <!-- 右列：商业诊断 + 营销策略 + 点子 + 大师库 -->
      <el-col :xs="24" :md="12">
        <el-card shadow="never" style="margin-bottom: 16px">
          <template #header>
            <div class="header-bar">
              <span>🏥 商业诊断（生意问题消解）</span>
              <el-button size="small" type="primary" :loading="diagLoading" @click="genDiagnosis">⚡ 诊断</el-button>
            </div>
          </template>
          <el-input v-model="diagQuestion" type="textarea" :rows="3" placeholder="描述你（或客户）遇到的生意问题，如：开了3年餐饮门店，客流越来越少，要不要转型做外卖？" />
          <div v-if="diagResult" class="diag-result">
            <div class="angle-line"><b>类型：</b>{{ diagResult.category }} <span v-if="diagResult.industry">{{ diagResult.industry }}</span></div>
            <div class="angle-line"><b>判断：</b>{{ diagResult.verdict }}</div>
            <div v-if="diagResult.reframes.length" class="diag-reframe">
              <div v-for="(r, i) in diagResult.reframes" :key="i" class="hook-variant">❓ {{ r }}</div>
            </div>
            <div class="master-warn">✅ {{ diagResult.action }}</div>
          </div>
        </el-card>

        <el-card shadow="never" style="margin-bottom: 16px">
          <template #header>
            <div class="header-bar">
              <span>🏛️ 营销策略（定位→钩子→信任→转化）</span>
              <el-button size="small" type="primary" :loading="strategyLoading" @click="genStrategy">⚡ 生成</el-button>
            </div>
          </template>
          <div v-loading="strategyLoading">
            <div v-for="(s, i) in strategy" :key="i" class="strategy-item">
              <div class="strategy-layer">{{ s.layer }}</div>
              <div class="strategy-advice">{{ s.advice }}</div>
            </div>
            <el-empty v-if="!strategyLoading && strategy.length === 0" description="选择行业后生成策略" :image-size="60" />
          </div>
        </el-card>

        <el-card shadow="never" style="margin-bottom: 16px">
          <template #header>
            <div class="header-bar">
              <span>💡 营销点子</span>
              <el-button size="small" type="primary" :loading="ideasLoading" @click="genIdeas">⚡ 生成</el-button>
            </div>
          </template>
          <div v-loading="ideasLoading">
            <div v-for="(idea, i) in ideas" :key="i" class="idea-item">{{ i + 1 }}. {{ idea }}</div>
            <el-empty v-if="!ideasLoading && ideas.length === 0" description="生成营销点子" :image-size="60" />
          </div>
        </el-card>

        <el-card shadow="never">
          <template #header><span>🧠 大师思想库（点击展开）</span></template>
          <el-collapse>
            <el-collapse-item v-for="m in frameworks" :key="m.name" :title="`${m.name} · ${m.tag}`">
              <div class="master-core">{{ m.core }}</div>
              <div class="master-apply">应用：{{ m.apply }}</div>
              <div v-if="m.warn" class="master-warn">⚠️ {{ m.warn }}</div>
            </el-collapse-item>
          </el-collapse>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'MarketingGuru',
  data() {
    return {
      industry: '',
      industryNames: [],
      frameworks: [],
      // 话术工坊
      kind: 'dm',
      style: 'standard',
      day: 3,
      count: 6,
      statsText: '',
      copyItems: [],
      copyLoading: false,
      // 策略/点子/角度
      strategy: [],
      strategyLoading: false,
      ideas: [],
      ideasLoading: false,
      leadNote: '',
      angleResult: null,
      angleLoading: false,
      // 商业诊断 + 内容钩子
      diagQuestion: '',
      diagResult: null,
      diagLoading: false,
      hookTopic: '',
      hookMaterial: '',
      hookResult: null,
      hookLoading: false
    }
  },
  async mounted() {
    this.loadIndustries()
    this.loadFrameworks()
  },
  methods: {
    async loadIndustries() {
      try {
        const res = await api.get('/industries/')
        this.industryNames = res.names || []
      } catch (e) { /* 忽略 */ }
    },
    async loadFrameworks() {
      try {
        const res = await api.get('/marketing/frameworks')
        this.frameworks = res.frameworks || []
      } catch (e) { /* 忽略 */ }
    },
    onKindChange() {
      this.copyItems = []
    },
    parseStats() {
      // "服务200+印花厂、8年源头" → {"clients":"服务200+印花厂","years":"8年源头"}
      const stats = {}
      const parts = (this.statsText || '').split(/[、,，;；]/).filter(Boolean)
      parts.forEach((p, i) => { stats['s' + i] = p })
      return Object.keys(stats).length ? stats : null
    },
    async genCopy() {
      this.copyLoading = true
      this.copyItems = []
      try {
        const body = {
          industry: this.industry,
          kind: this.kind,
          count: this.count,
          style: this.style,
          day: this.day
        }
        if (this.kind === 'dm' && this.statsText) body.stats = this.parseStats()
        const res = await api.post('/marketing/copy', body)
        this.copyItems = res.items || []
      } catch (e) {
        this.$message.error('生成失败：' + e.message)
      } finally {
        this.copyLoading = false
      }
    },
    async genStrategy() {
      this.strategyLoading = true
      this.strategy = []
      try {
        const res = await api.post('/marketing/strategy', { industry: this.industry, goal: '获客' })
        this.strategy = res.strategy || []
      } catch (e) {
        this.$message.error('生成失败：' + e.message)
      } finally {
        this.strategyLoading = false
      }
    },
    async genIdeas() {
      this.ideasLoading = true
      this.ideas = []
      try {
        const res = await api.post('/marketing/ideas', { industry: this.industry, count: 10 })
        this.ideas = res.ideas || []
      } catch (e) {
        this.$message.error('生成失败：' + e.message)
      } finally {
        this.ideasLoading = false
      }
    },
    async genAngle() {
      if (!this.leadNote.trim()) {
        this.$message.warning('请先粘贴客户留言')
        return
      }
      this.angleLoading = true
      try {
        const res = await api.post('/marketing/angle', { industry: this.industry, lead_note: this.leadNote })
        this.angleResult = res
      } catch (e) {
        this.$message.error('推荐失败：' + e.message)
      } finally {
        this.angleLoading = false
      }
    },
    copyText(text) {
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      this.$message.success('已复制')
    },
    async genDiagnosis() {
      if (!this.diagQuestion.trim()) {
        this.$message.warning('请先描述生意问题')
        return
      }
      this.diagLoading = true
      try {
        const res = await api.post('/marketing/diagnosis', { question: this.diagQuestion, industry: this.industry })
        this.diagResult = res
      } catch (e) {
        this.$message.error('诊断失败：' + e.message)
      } finally {
        this.diagLoading = false
      }
    },
    async genHook() {
      if (!this.hookTopic.trim()) {
        this.$message.warning('请先输入内容主题')
        return
      }
      this.hookLoading = true
      try {
        const res = await api.post('/marketing/hook', { topic: this.hookTopic, material: this.hookMaterial, industry: this.industry })
        this.hookResult = res
      } catch (e) {
        this.$message.error('生成失败：' + e.message)
      } finally {
        this.hookLoading = false
      }
    }
  }
}
</script>

<style scoped>
.header-bar { display: flex; justify-content: space-between; align-items: center; }
.industry-sel { width: 220px; }
.hb-sel-wrap { flex: 1; min-width: 0; display: flex; justify-content: flex-end; }
@media (max-width: 767px) {
  .industry-sel { width: 100%; }
  .hb-sel-wrap { justify-content: stretch; }
}
.opt-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.opt-hint { font-size: 12px; color: #909399; }
.copy-item { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; padding: 8px 10px; background: var(--el-fill-color, #f5f7fa); border-radius: 6px; margin-bottom: 8px; }
.copy-text { font-size: 13px; line-height: 1.6; flex: 1; }
.strategy-item { padding: 10px 12px; background: rgba(34,197,94,.1); border: 1px solid rgba(34,197,94,.18); border-radius: 6px; margin-bottom: 8px; }
.strategy-layer { font-weight: bold; font-size: 13px; color: #67c23a; margin-bottom: 4px; }
.strategy-advice { font-size: 13px; line-height: 1.6; }
.idea-item { padding: 8px 10px; border-bottom: 1px dashed var(--el-border-color-light, #ebeef5); font-size: 13px; line-height: 1.6; }
.angle-result { margin-top: 12px; padding: 10px 12px; background: rgba(245,158,11,.08); border: 1px solid rgba(245,158,11,.2); border-radius: 6px; font-size: 13px; line-height: 1.8; }
.angle-line b { color: #e6a23c; }
.master-core { font-size: 13px; line-height: 1.7; }
.master-apply { font-size: 13px; color: #67c23a; margin-top: 6px; line-height: 1.7; }
.master-warn { font-size: 12px; color: #e6a23c; margin-top: 6px; }
.hook-result, .diag-result { margin-top: 12px; padding: 10px 12px; background: rgba(34,197,94,.1); border: 1px solid rgba(34,197,94,.18); border-radius: 6px; font-size: 13px; line-height: 1.8; }
.hook-variant { padding: 6px 8px; background: var(--el-bg-color-overlay, #fff); border: 1px solid var(--el-border-color-light, transparent); border-radius: 4px; margin: 6px 0; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.diag-reframe { margin-top: 6px; }
.angle-line b { color: #e6a23c; }
@media (max-width: 767px) {
  .header-bar { flex-wrap: wrap; gap: 8px; }
  .header-bar :deep(.el-select) { width: 100% !important; }
  .opt-row { flex-direction: column; align-items: stretch; }
  .opt-row :deep(.el-select), .opt-row :deep(.el-input), .opt-row :deep(.el-input-number) { width: 100% !important; }
  .header-bar :deep(.el-button) { margin-left: 0 !important; }
}
</style>
